import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Download, ChevronDown, Calendar, Save, Loader2, ArrowLeft, DollarSign, Clock, LayoutGrid, Search, FileText } from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { shiftTypesForDuty } from '../constants/shifts';

// --- CONSTANTS & CONFIG ---
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = [2024, 2025, 2026, 2027];

// --- INTERFACES ---
interface Nurse {
  id: number;
  name?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
}

interface AssignmentAPI {
  id: number;
  start_date: string;
  end_date: string;
  shift_type: string;
  session: string;
  rate: number;
  total_charge?: number;
  actual_hours?: number;
  second_actual_hours?: number;
  nurse_id?: number;   physio_id?: number;
  nurse?:  { id: number; full_name?: string };
  physio?: { id: number; full_name?: string };
  second_nurse_id?: number; second_physio_id?: number;
  second_nurse?:  { id: number; full_name?: string };
  second_physio?: { id: number; full_name?: string };
  second_shift_type?: string;
  second_rate?: number;
  second_total_charge?: number;
}

interface AssignmentEntry {
  record_id?: number; 
  assignment_id?: number; 
  name: string;
  shift_type: string;
  rate: number;
  shifts?: number;
  hrs?: number; 
  hidden_actual_hours?: number;
  amount?: number; 
  nurse_id?: number;
  physio_id?: number;
  is_deleted?: boolean;
  is_dirty?: boolean;
}

interface ShiftDetail {
  type: string;
  entries: AssignmentEntry[];
  nurse_count: number;
  total_cost_for_shift: number;
}

interface DailyRecord {
  date_str: string;
  day_display: string;
  day_of_week: string;
  shifts: Record<string, ShiftDetail>;
}

interface DropdownOption {
    label: string;
    value: any;
}

interface SearchableDropdownProps {
    options: DropdownOption[];
    value: any;
    onChange: (val: any) => void;
    placeholder: string;
    onDelete: () => void;
}

// --- HELPERS ---
const getNurseName = (nurse: any): string => {
    if (!nurse) return '';
    if (nurse.full_name) return nurse.full_name;
    if (nurse.first_name) return `${nurse.first_name} ${nurse.last_name || ''}`;
    if (nurse.name) return nurse.name;
    return `ID: ${nurse.id}`;
};

const getClientName = (patient: any): string => {
    if (!patient) return 'Direct Client';
    if (typeof patient.client_name === 'string' && patient.client_name) return patient.client_name;
    const possibleArrays = [patient.clients_info, patient.patient_clients, patient.clients, patient.client_info];
    for (const arr of possibleArrays) {
        if (Array.isArray(arr) && arr.length > 0) {
            return arr[0].client_name || arr[0].name || arr[0].full_name || 'Direct Client';
        }
    }
    if (patient.client && typeof patient.client === 'object') {
        return patient.client.client_name || patient.client.name || 'Direct Client';
    }
    return 'Direct Client';
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultHours = (shiftType: string): number => {
    if (!shiftType) return 0;
    const lower = shiftType.toLowerCase();
    if (lower.includes('08')) return 8;
    if (lower.includes('12')) return 12;
    if (lower.includes('24')) return 24;
    return 0; // Fallback
};

// --- SEARCHABLE DROPDOWN ---
const SearchableDropdown: React.FC<SearchableDropdownProps> = ({ options, value, onChange, placeholder, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
        }
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const actionOption = options.find((o: DropdownOption) => o.value === 'ADD_NURSE');
    const dataOptions = options.filter((o: DropdownOption) => o.value !== 'ADD_NURSE');
    const filteredOptions = dataOptions.filter((o: DropdownOption) => o.label.toLowerCase().includes(searchTerm.toLowerCase()));
    const selectedOption = options.find((o: DropdownOption) => o.value === value);
    const isNoDuty = !selectedOption || selectedOption.label === 'No Duty';
    const currentTextColor = isNoDuty ? '#dc2626' : '#0f172a'; // Red if empty, Dark Blue if nurse selected
    const currentFontWeight = isNoDuty ? 700 : 600; // Bold if empty

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
            <div onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }} 
                style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, color: selectedOption ? '#0f172a' : '#94a3b8' }}>
                <span style={{ 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis', 
                    whiteSpace: 'nowrap',
                    color: currentTextColor,       // <-- ADDED
                    fontWeight: currentFontWeight  // <-- ADDED
                }}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    {value && onDelete && <span onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ color: '#ef4444', fontSize: '16px', padding: '0 4px', fontWeight: 'bold' }}>×</span>}
                    <ChevronDown size={14} color="#64748b" />
                </div>
            </div>
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, width: '220px', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', zIndex: 9999, marginTop: '4px' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Search size={14} color="#94a3b8" />
                        <input autoFocus type="text" placeholder="Search Nurse..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.8rem' }} />
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {actionOption && (
                            <div onClick={(e) => { e.stopPropagation(); onChange(actionOption.value); setIsOpen(false); setSearchTerm(''); }}
                                style={{ padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #cbd5e1', backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 700, position: 'sticky', top: 0, zIndex: 10 }}>
                                {actionOption.label}
                            </div>
                        )}
                        {filteredOptions.length > 0 ? filteredOptions.map((opt: DropdownOption, i: number) => (
                            <div key={i} onClick={(e) => { e.stopPropagation(); onChange(opt.value); setIsOpen(false); setSearchTerm(''); }}
                                style={{ padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: value === opt.value ? '#eff6ff' : 'white', color: value === opt.value ? '#2563eb' : '#334155', fontWeight: 400 }}>
                                {opt.label}
                            </div>
                        )) : <div style={{ padding: '8px 12px', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center' }}>No results found</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

const Casesheet: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const patientId = Number(id);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const qMonth = searchParams.get('month');
    const qYear = searchParams.get('year');
    return (qMonth && qYear) ? new Date(parseInt(qYear), parseInt(qMonth) - 1, 1) : new Date();
  });

  const [patient, setPatient] = useState<any>(null);
  const [records, setRecords] = useState<DailyRecord[]>([]);
  const [shiftsConfig, setShiftsConfig] = useState<string[]>([]); 
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hoverRowId, setHoverRowId] = useState<string | null>(null);
  const [allNurses, setAllNurses] = useState<Nurse[]>([]);
  const [allPhysios, setAllPhysios] = useState<Nurse[]>([]);
  const [staffDataLoaded, setStaffDataLoaded] = useState(false);

  const updateDateAndUrl = (newDate: Date) => {
      setCurrentDate(newDate);
      navigate(`/casesheet/${patientId}?month=${newDate.getMonth() + 1}&year=${newDate.getFullYear()}`, { replace: true });
  };

  const handleDropdownChange = (type: 'month' | 'year', value: number) => {
      const newDate = new Date(currentDate);
      if (type === 'month') newDate.setMonth(value);
      if (type === 'year') newDate.setFullYear(value);
      updateDateAndUrl(newDate);
  }

  useEffect(() => {
    const fetchData = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const headers = { 'Authorization': `Bearer ${token}` };
            const [ptRes, nrRes, physioRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/api/patients/${patientId}`, { headers }),
                axios.get(`${API_BASE_URL}/api/nurses/`, { headers }),
                axios.get(`${API_BASE_URL}/api/physiotherapists/`, { headers })
            ]);
            setPatient(ptRes.data);
            setAllNurses(Array.isArray(nrRes.data) ? nrRes.data : (nrRes.data.results || []));
            setAllPhysios(Array.isArray(physioRes.data) ? physioRes.data : (physioRes.data.results || []));
            setStaffDataLoaded(true);
        } catch (e) { console.error(e); }
    };
    if (patientId) fetchData();
  }, [patientId, API_BASE_URL]);

  const fetchAssignments = useCallback(async () => {
  if (!patientId) return;
  setLoading(true);
  try {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    const startDateStr = `${year}-${String(month).padStart(2, '0')}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const endDateStr = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    const token = localStorage.getItem('accessToken');
    const headers = { 'Authorization': `Bearer ${token}` };
    
    // NEW two-layer flow: case sheet reads shift_actuals (downstream of the grid).
    const [actRes, prefRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/schedule/casesheet/${patientId}`, { params: { start: startDateStr, end: endDateStr }, headers }),
        axios.get(`${API_BASE_URL}/api/patients/${patientId}/shift-preferences`, { headers }).catch(() => ({ data: { shift_order: [] } }))
    ]);

    const actuals: any[] = Array.isArray(actRes.data) ? actRes.data : [];
    const savedOrder = prefRes.data?.shift_order || [];

    // --- 1. MASTER COLUMN SORTING ---
    const PREDEFINED_ORDER = ['08 Morning', '08 Afternoon', '08 Night', '12 Day', '12 Night', '24 Hrs', 'Morning Visit', 'Afternoon Visit', 'Evening Visit'];

    const activeShiftTypes = new Set<string>();
    actuals.forEach((a: any) => { if (a.shift_type) activeShiftTypes.add(a.shift_type); });

    if (activeShiftTypes.size === 0) {
        // Canonical duty → shift columns (constants/shifts.ts) — fixes 12 Day/12 Night/
        // Physiotherapy previously defaulting to three 8-hour columns.
        shiftTypesForDuty(patient?.duty_type).forEach(t => activeShiftTypes.add(t));
    }

    let sortedShifts = Array.from(activeShiftTypes).sort((a, b) => {
        const indexA = PREDEFINED_ORDER.indexOf(a);
        const indexB = PREDEFINED_ORDER.indexOf(b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
    });

    if (savedOrder.length > 0 && typeof savedOrder[0] === 'object') {
        sortedShifts = savedOrder.map((s: any) => s.type);
    }
    setShiftsConfig(sortedShifts);

    // Resolve the staff to DISPLAY: casesheet override staff if set, else schedule staff.
    const displayStaff = (a: any) => {
        const nId = a.casesheet_nurse_id || a.nurse_id;
        const pId = a.casesheet_physio_id || a.physio_id;
        if (nId) {
            const obj = a.casesheet_nurse || a.nurse || allNurses.find(n => n.id === nId);
            return { nurse_id: nId, physio_id: undefined, name: obj ? getNurseName(obj) : `ID: ${nId}` };
        }
        if (pId) {
            const obj = a.casesheet_physio || a.physio || allPhysios.find(p => p.id === pId);
            return { nurse_id: undefined, physio_id: pId, name: obj ? getNurseName(obj) : `ID: ${pId}` };
        }
        return { nurse_id: undefined, physio_id: undefined, name: '' };
    };

    // --- 2. POPULATE DAILY RECORDS from actuals ---
    const byDateShift: Record<string, Record<string, any[]>> = {};
    actuals.forEach((a: any) => {
        const d = String(a.date).split('T')[0];
        if (!byDateShift[d]) byDateShift[d] = {};
        if (!byDateShift[d][a.shift_type]) byDateShift[d][a.shift_type] = [];
        byDateShift[d][a.shift_type].push(a);
    });

    const newRecords: DailyRecord[] = [];
    for (let i = 1; i <= lastDay; i++) {
      const dateObj = new Date(year, month - 1, i);
      const dateStr = formatDate(dateObj);
      const dayRecord: Record<string, ShiftDetail> = {};

      sortedShifts.forEach(shiftType => {
          const rows = (byDateShift[dateStr]?.[shiftType]) || [];
          const entries: AssignmentEntry[] = rows
            .filter(a => !a.is_omitted_casesheet)
            .sort((x, y) => (x.role === 'primary' ? 0 : 1) - (y.role === 'primary' ? 0 : 1))
            .map(a => {
              const stdHrs = getDefaultHours(shiftType) || 1;
              const rate = a.client_charge_rate ?? 0;
              const st = displayStaff(a);
              return {
                  record_id: a.id,
                  assignment_id: a.source_shift_id,
                  name: st.name,
                  shift_type: shiftType,
                  rate, hrs: stdHrs,
                  hidden_actual_hours: a.actual_hours,
                  amount: rate,
                  shifts: a.shifts_count !== null && a.shifts_count !== undefined ? a.shifts_count : 1,
                  nurse_id: st.nurse_id,
                  physio_id: st.physio_id,
                  is_deleted: false,
              } as AssignmentEntry;
          });
          dayRecord[shiftType] = { type: shiftType, entries, nurse_count: entries.length, total_cost_for_shift: entries.reduce((sum, e) => sum + Number(e.amount || 0), 0) };
      });

      newRecords.push({ date_str: dateStr, day_display: String(i).padStart(2, '0'), day_of_week: dateObj.toLocaleDateString('en-US', { weekday: 'short' }), shifts: dayRecord });
    }
    setRecords(newRecords);
  } catch (error) { console.error("Fetch Error:", error); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentDate, patientId, API_BASE_URL, allNurses, allPhysios]);

  useEffect(() => { if (staffDataLoaded) fetchAssignments(); }, [fetchAssignments, staffDataLoaded]);

  const calculateRow = (record: DailyRecord) => {
    const shiftValues = Object.values(record.shifts);
    return { 
        // 👇 Change this line so it sums up the actual inputs
        activeShifts: shiftValues.reduce((acc, curr) => 
            acc + curr.entries.reduce((sum, e) => sum + Number(e.shifts || 0), 0)
        , 0), 
        totalAmount: shiftValues.reduce((acc, curr) => acc + curr.total_cost_for_shift, 0) 
    };
  };

  const grandTotal = useMemo(() => records.reduce((acc, curr) => {
    const row = calculateRow(curr);
    return { shifts: acc.shifts + row.activeShifts, amount: acc.amount + row.totalAmount };
  }, { shifts: 0, amount: 0 }), [records]);

  // --- EXPORT HANDLERS ---
  const handleExportCSV = () => {
    const monthName = MONTHS[currentDate.getMonth()];
    const clientName = getClientName(patient);
    const headers = ["Date", "Day", ...shiftsConfig.flatMap(s => [`${s} Nurse`, `${s} Rate`]), "Total Shifts", "Total Amount"];
    const csvRows = records.map(r => {
        const { activeShifts, totalAmount } = calculateRow(r);
        const shiftData = shiftsConfig.flatMap(sType => {
            const s = r.shifts[sType];
            const names = s?.entries.map((e: AssignmentEntry) => e.name).join(' & ') || "";
            const rates = s?.total_cost_for_shift || 0;
            return [`"${names}"`, rates];
        });
        return [r.date_str, r.day_of_week, ...shiftData, activeShifts, totalAmount].join(",");
    });
    const csvContent = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${clientName.replace(/\s/g, '_')} - Case Sheet - ${monthName}.csv`);
  };

  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Case Sheet');
    const monthName = MONTHS[currentDate.getMonth()];
    const shortMonth = currentDate.toLocaleDateString('en-US', { month: 'short' });
    const clientName = getClientName(patient);
    const cleanClientName = clientName.replace(/\s/g, '_');

    // Define Columns
    const columns = [
        { header: 'Date', key: 'date', width: 15 },
        { header: 'Week', key: 'week', width: 10 },
    ];

    shiftsConfig.forEach(s => {
        columns.push({ header: `${s} Nurse`, key: `${s}_nurse`, width: 30 });
        columns.push({ header: `Hrs`, key: `${s}_hrs`, width: 12 });
        columns.push({ header: `Rate`, key: `${s}_rate`, width: 12 });
    });

    columns.push({ header: 'Total Shifts', key: 'total_shifts', width: 12 });
    columns.push({ header: 'Total Amount', key: 'total_amount', width: 15 });
    
    worksheet.columns = columns;

    // --- NEW: INSERT BIG HEADERS AT THE TOP ---
    worksheet.spliceRows(1, 0, [], []); // Push the columns down 2 rows
    const totalColsCount = columns.length;

    // Big Header Row 1: Client Name & Month
    worksheet.mergeCells(1, 1, 1, totalColsCount);
    const row1 = worksheet.getRow(1);
    row1.height = 30;
    row1.getCell(1).value = `${clientName} / ${shortMonth} ${currentDate.getFullYear()}`;
    row1.getCell(1).font = { size: 16, bold: true, color: { argb: 'FF000000' } };
    row1.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

    // Big Header Row 2: Case Start Date
    worksheet.mergeCells(2, 1, 2, totalColsCount);
    const row2 = worksheet.getRow(2);
    row2.height = 25;
    
    // Format "5th Feb 2026"
    let caseStartDisplay = '-';
    if (patient?.case_start_date) {
        const sd = new Date(patient.case_start_date);
        const suffix = ['th', 'st', 'nd', 'rd'][((sd.getDate() + 90) % 100 - 10) % 10 - 1] || 'th';
        caseStartDisplay = `${sd.getDate()}${suffix} ${sd.toLocaleDateString('en-US', { month: 'short' })} ${sd.getFullYear()}`;
    }
    row2.getCell(1).value = `Case Start Date : ${caseStartDisplay}`;
    row2.getCell(1).font = { size: 16, bold: true, color: { argb: 'FF1E3A8A' } }; // Blue text
    row2.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

    // Add borders to the big header
    for(let i = 1; i <= totalColsCount; i++) {
        row1.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
        row2.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
    }
    // --- END NEW HEADERS ---


    // Style Header Row: (Now pushed to Row 3)
    const headerRow = worksheet.getRow(3);
    headerRow.height = 30;
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { 
            top: { style: 'thin' }, left: { style: 'thin' }, 
            bottom: { style: 'thin' }, right: { style: 'thin' } 
        };
    });

    // Populate Rows — one sub-row per nurse entry so assistants get their own line
    const totalShiftsColNum = 2 + shiftsConfig.length * 3 + 1;
    const totalAmountColNum = 2 + shiftsConfig.length * 3 + 2;

    records.forEach(r => {
        const { activeShifts, totalAmount } = calculateRow(r);

        const dObj = new Date(r.date_str);
        const displayDate = `${dObj.getDate()}-${dObj.toLocaleDateString('en-US', { month: 'short' })}-${dObj.getFullYear().toString().slice(-2)}`;

        const maxEntries = Math.max(1, ...shiftsConfig.map(s => r.shifts[s]?.entries.length || 0));
        const startRowNum = worksheet.rowCount + 1;

        for (let i = 0; i < maxEntries; i++) {
            const rowData: any = {};

            if (i === 0) {
                rowData.date = displayDate;
                rowData.week = r.day_of_week;
                rowData.total_shifts = activeShifts;
                rowData.total_amount = totalAmount;
            }

            shiftsConfig.forEach(s => {
                const entry = r.shifts[s]?.entries[i];
                rowData[`${s}_nurse`] = entry?.name || '';
                rowData[`${s}_hrs`]   = entry?.hrs ?? '';
                rowData[`${s}_rate`]  = entry ? (entry.amount ?? entry.rate ?? 0) : '';
            });

            const row = worksheet.addRow(rowData);
            row.eachCell((cell, colNumber) => {
                cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
                if (colNumber === 1 && r.day_of_week === 'Sun') {
                    cell.font = { color: { argb: 'FFDC2626' } };
                }
            });
        }

        // Merge across sub-rows when any shift has an assistant
        if (maxEntries > 1) {
            const endRowNum = worksheet.rowCount;

            // Always merge date, week, and totals
            const alwaysMerge = [1, 2, totalShiftsColNum, totalAmountColNum];
            alwaysMerge.forEach(col => {
                worksheet.mergeCells(startRowNum, col, endRowNum, col);
                worksheet.getCell(startRowNum, col).alignment = { horizontal: 'center', vertical: 'middle' };
            });

            // For each shift that has only 1 entry (no assistant), merge its 3 columns
            shiftsConfig.forEach((s, idx) => {
                const entryCount = r.shifts[s]?.entries.length || 0;
                if (entryCount < maxEntries) {
                    const baseCol = 3 + idx * 3;
                    [baseCol, baseCol + 1, baseCol + 2].forEach(col => {
                        worksheet.mergeCells(startRowNum, col, endRowNum, col);
                        worksheet.getCell(startRowNum, col).alignment = { horizontal: 'center', vertical: 'middle' };
                    });
                }
            });
        }
    });

    // Add Grand Total Row
    const lastRowIndex = worksheet.rowCount + 1;
    const totalRow = worksheet.addRow([]);
    const span = 2 + (shiftsConfig.length * 3);
    
    // Merge label cell
    worksheet.mergeCells(lastRowIndex, 1, lastRowIndex, span);
    const labelCell = totalRow.getCell(1);
    labelCell.value = `GRAND TOTAL (${monthName} ${currentDate.getFullYear()})`;
    labelCell.alignment = { horizontal: 'right', vertical: 'middle' };
    
    // Set total values
    totalRow.getCell(span + 1).value = grandTotal.shifts;
    totalRow.getCell(span + 2).value = grandTotal.amount;

    // Style Total Row: Gold Background, Bold Text
    totalRow.eachCell((cell, colNumber) => {
        if (colNumber >= 1 && colNumber <= span + 2) {
            cell.font = { bold: true };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCD34D' } };
            cell.border = { 
                top: { style: 'thin' }, left: { style: 'thin' }, 
                bottom: { style: 'thin' }, right: { style: 'thin' } 
            };
        }
    });

    // Generate and Save
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `${cleanClientName} - Case Sheet - ${monthName}.xlsx`);
  };

  const handleCellChange = (dateStr: string, shiftType: string, index: number, field: keyof AssignmentEntry, value: any) => {
  setRecords(prev => prev.map(r => {
      if (r.date_str !== dateStr) return r;
      
      const newEntries = [...r.shifts[shiftType].entries];
      if (!newEntries[index]) {
        const defaultH = getDefaultHours(shiftType);
        newEntries[index] = { name: '', shift_type: shiftType, rate: 0, hrs: defaultH, amount: 0, is_deleted: false };
      }
      
      const parsedValue = (field === 'rate' || field === 'hrs' || field === 'amount' || field === 'shifts') ? Number(value) : value;
      newEntries[index] = { ...newEntries[index], [field]: parsedValue, is_dirty: true };
      
      // Auto-calculate amount if hrs or rate changes
      if (field === 'hrs' || field === 'rate') {
          const currentHrs = Number(newEntries[index].hrs) || 0;
          const currentRate = Number(newEntries[index].rate) || 0;
          const standardHrs = getDefaultHours(shiftType) || 1; 
          
          // Proportional math:
          newEntries[index].amount = (currentRate / standardHrs) * currentHrs;
      }

      // Auto-fill shifts to 1 when staff is selected
      if ((field === 'nurse_id' || field === 'physio_id') && value) {
          if (!newEntries[index].shifts) {
              newEntries[index].shifts = 1;
          }
      }
      
      // Calculate the new total for the whole shift
      const newTotalCost = newEntries.reduce((sum, e) => sum + Number(e.amount || 0), 0);

      return {
          ...r,
          shifts: {
              ...r.shifts,
              [shiftType]: {
                  ...r.shifts[shiftType],
                  entries: newEntries,
                  total_cost_for_shift: newTotalCost // This now correctly sums the 'Amount' column
              }
          }
      };
  }));
};

  const handleAddNurseToSession = (dateStr: string, shiftType: string) => {
    setRecords(prev => prev.map(r => {
        if (r.date_str !== dateStr) return r;
        
        return {
            ...r,
            shifts: {
                ...r.shifts,
                [shiftType]: {
                      ...r.shifts[shiftType],
                      entries: [
                        ...r.shifts[shiftType].entries, 
                        { 
                            name: '', 
                            shift_type: shiftType, 
                            rate: 0, 
                            shifts: 1,
                            hrs: getDefaultHours(shiftType), // Auto-fill here
                            amount: 0, 
                            is_dirty: true, 
                            is_deleted: false 
                        }
                    ]
                }
            }
        };
    }));
  };

  // --- BUG FIX #1: Don't erase Master Assignments, mark them as deleted ---
  const handleDeleteNurse = (dateStr: string, shiftType: string, index: number) => {
    // if (!window.confirm("Remove this nurse assignment?")) return;
    setRecords(prev => prev.map(r => {
        if (r.date_str !== dateStr) return r;
        
        const newEntries = [...r.shifts[shiftType].entries];
        if (newEntries[index]) {
            // If it has a record_id OR an assignment_id, keep it in the array but mark it as deleted
            if (newEntries[index].record_id || newEntries[index].assignment_id) {
                newEntries[index] = { ...newEntries[index], is_deleted: true, is_dirty: true };
            } else {
                // If it's a completely unsaved, brand-new row, we can just erase it
                newEntries.splice(index, 1);
            }
        }
        
        const activeEntries = newEntries.filter(e => !e.is_deleted);
        
        return {
            ...r,
            shifts: {
                ...r.shifts,
                [shiftType]: {
                    ...r.shifts[shiftType],
                    entries: newEntries,
                    nurse_count: activeEntries.length,
                    total_cost_for_shift: activeEntries.reduce((sum, e) => sum + Number(e.amount || 0), 0)
                }
            }
        };
    }));
  };

  // --- BUG FIX #2: Ensure the deletion gets pushed to the payload ---
  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('accessToken');
    const headers = { 'Authorization': `Bearer ${token}` };
    const promises: Promise<any>[] = [];

    // One-way: PATCH only the casesheet side of each backing actual. Reassigning the
    // staff writes casesheet_nurse_id/casesheet_physio_id (billing-only) — the schedule
    // and salary are untouched. Rows without a record_id (manually added) can't persist;
    // shifts come from the grid in the new flow.
    records.forEach(r => {
        Object.values(r.shifts).forEach(shift => {
            shift.entries.forEach(e => {
                if (!e.is_dirty) return;
                if (!e.record_id) return; // no backing actual → not persistable here

                promises.push(axios.patch(`${API_BASE_URL}/api/schedule/actuals/${e.record_id}/casesheet`, {
                    casesheet_nurse_id:  e.nurse_id  || null,
                    casesheet_physio_id: e.physio_id || null,
                    actual_hours: e.hidden_actual_hours !== undefined && e.hidden_actual_hours !== null
                        ? e.hidden_actual_hours
                        : Number(e.hrs || 0),
                    client_charge_rate: Number(e.rate || 0),
                    shifts_count: Number(e.shifts || 0),
                    is_omitted_casesheet: e.is_deleted || false,
                }, { headers }));
            });
        });
    });

    try {
        if (promises.length === 0) { setSaving(false); return alert("No changes to save."); }
        await Promise.all(promises);
        alert("Saved successfully!");
        fetchAssignments();
    } catch (e) {
        alert("Save error.");
    } finally {
        setSaving(false);
    }
  };

  const allStaffOptions: DropdownOption[] = [
      ...allNurses.map((n: Nurse)  => ({ label: getNurseName(n),  value: `n_${n.id}` })),
      ...allPhysios.map((p: Nurse) => ({ label: `${getNurseName(p)} (Physio)`, value: `p_${p.id}` })),
  ];

  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
    title: { margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' },
    subtitle: { margin: 0, color: '#64748b', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    primaryBtn: { backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '8px', padding: '0.75rem 1.25rem', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    exportBtn: { backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    excelBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '2rem' },
    statCard: { backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '1rem' },
    tableCard: { backgroundColor: 'white', borderRadius: '8px', border: '1px solid #cbd5e1', overflow: 'hidden' },
    tableWrapper: { overflowX: 'auto', paddingBottom: '100px' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' },
    thGroup: { padding: '0.6rem', border: '1px solid #cbd5e1', backgroundColor: '#e2e8f0', textAlign: 'center', fontWeight: 700, textTransform: 'uppercase' },
    th: { padding: '0.5rem', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', textAlign: 'center', fontWeight: 600 },
    td: { border: '1px solid #cbd5e1', verticalAlign: 'middle', height: '36px' },
    cellInput: { width: '100%', border: 'none', textAlign: 'center', outline: 'none', background: 'transparent' }
  };

  return (
    <div style={styles.page}>
      <button style={{ border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', cursor: 'pointer', marginBottom: '1rem' }} onClick={() => navigate('/casesheet')}>
         <ArrowLeft size={18} /> Back
      </button>

      <header style={styles.header}>
        <div>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Case Sheet</span>
            <h1 style={styles.title}>{getClientName(patient)} / {patient?.patient_name || 'Loading...'}</h1>
            <div style={{ display: 'flex', gap: '20px', marginTop: '4px' }}>
                <p style={styles.subtitle}><Calendar size={14} /> Start: {patient?.case_start_date || '-'}</p>
                <p style={styles.subtitle}><LayoutGrid size={14} /> Duty: {patient?.duty_type || '-'}</p>
            </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <select style={{ padding: '8px', borderRadius: '6px' }} value={currentDate.getMonth()} onChange={e => handleDropdownChange('month', Number(e.target.value))}>
                {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select style={{ padding: '8px', borderRadius: '6px' }} value={currentDate.getFullYear()} onChange={e => handleDropdownChange('year', Number(e.target.value))}>
                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <button style={styles.exportBtn} onClick={handleExportCSV} title="Export to CSV"><Download size={16} /> CSV</button>
            <button style={styles.excelBtn} onClick={handleExportExcel} title="Export to XLSX"><FileText size={16} /> Excel</button>
            <button style={styles.primaryBtn} disabled={saving} onClick={handleSave}>
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
            </button>
        </div>
      </header>

      <div style={styles.statsGrid}>
          <div style={styles.statCard}>
              <div style={{ color: '#3b82f6' }}><DollarSign size={24} /></div>
              <div><span style={{ fontSize: '1.5rem', fontWeight: 700 }}>₹{grandTotal.amount.toLocaleString()}</span><div style={{ fontSize: '0.8rem', color: '#64748b' }}>Total Billed</div></div>
          </div>
          <div style={styles.statCard}>
              <div style={{ color: '#22c55e' }}><Clock size={24} /></div>
              <div><span style={{ fontSize: '1.5rem', fontWeight: 700 }}>{grandTotal.shifts}</span><div style={{ fontSize: '0.8rem', color: '#64748b' }}>Total Shifts</div></div>
          </div>
      </div>

      <div style={styles.tableCard}>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{...styles.thGroup, width: '40px'}}>#</th>
                <th style={{...styles.thGroup, width: '50px'}}>Day</th>
                {shiftsConfig.map((s, i) => <th key={i} colSpan={5} style={styles.thGroup}>{s}</th>)}
                <th colSpan={2} style={styles.thGroup}>Summary</th>
              </tr>
              <tr>
                <th style={styles.th}>Date</th><th style={styles.th}>Wk</th>
                {shiftsConfig.map((_, i) => (
                  <React.Fragment key={i}>
                    <th style={{...styles.th, width: '180px'}}>Nurse Name</th>
                    <th style={{...styles.th, width: '50px'}}>Shifts</th> 
                    <th style={{...styles.th, width: '50px'}}>Hrs</th>
                    <th style={{...styles.th, width: '80px'}}>Rate</th>
                    <th style={{...styles.th, width: '90px'}}>Amount</th>
                  </React.Fragment>
                ))}
                <th style={styles.th}>Total Shifts</th><th style={styles.th}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={shiftsConfig.length * 3 + 4} style={{ padding: '4rem', textAlign: 'center' }}>Loading...</td></tr> : 
                records.map((row: DailyRecord) => {
                  const { activeShifts, totalAmount } = calculateRow(row);
                  return (
                    <tr key={row.date_str} onMouseEnter={() => setHoverRowId(row.date_str)} onMouseLeave={() => setHoverRowId(null)} style={{ backgroundColor: hoverRowId === row.date_str ? '#f1f5f9' : (['Sun', 'Sat'].includes(row.day_of_week) ? '#fafafa' : 'white') }}>
                      <td style={{...styles.td, textAlign: 'center', color: '#64748b'}}>{row.day_display}</td>
                      <td style={{...styles.td, textAlign: 'center', color: row.day_of_week === 'Sun' ? '#ef4444' : '#64748b', fontWeight: 600}}>{row.day_of_week}</td>
                      {shiftsConfig.map((sType: string, sIdx: number) => {
                          const shiftData: ShiftDetail = row.shifts[sType] || { type: sType, entries: [{ nurse_id: undefined, physio_id: undefined, name: '', shift_type: sType, rate: 0, is_deleted: false }], nurse_count: 0, total_cost_for_shift: 0 };
                          const entries: AssignmentEntry[] = shiftData.entries.length > 0 ? shiftData.entries : [{ nurse_id: undefined, physio_id: undefined, name: '', shift_type: sType, rate: 0, is_deleted: false }];
                          return (
                              <React.Fragment key={sIdx}>
                                <td style={styles.td}>
                                    {entries.map((e: AssignmentEntry, idx: number) => !e.is_deleted && (
                                        <div key={idx} style={{ height: '36px', borderBottom: idx < entries.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                                            {(() => {
                                                const dropVal = e.nurse_id ? `n_${e.nurse_id}` : e.physio_id ? `p_${e.physio_id}` : undefined;
                                                // Inject a fallback option if the staff member isn't in the loaded list
                                                const hasOption = !dropVal || allStaffOptions.some(o => o.value === dropVal);
                                                const baseOpts: DropdownOption[] = hasOption ? allStaffOptions : [...allStaffOptions, { value: dropVal!, label: e.name || `Staff #${e.nurse_id || e.physio_id}` }];
                                                const opts: DropdownOption[] = idx === entries.length - 1 && (e.nurse_id || e.physio_id) ? [...baseOpts, { label: '+ Add Second Staff', value: 'ADD_NURSE' }] : baseOpts;
                                                return (
                                                    <SearchableDropdown
                                                        options={opts}
                                                        value={dropVal}
                                                        onChange={(val: any) => {
                                                            if (val === 'ADD_NURSE') { handleAddNurseToSession(row.date_str, sType); return; }
                                                            if (!val) return;
                                                            const [kind, idStr] = String(val).split('_');
                                                            const id = Number(idStr);
                                                            const member = kind === 'n' ? allNurses.find(n => n.id === id) : allPhysios.find(p => p.id === id);
                                                            handleCellChange(row.date_str, sType, idx, kind === 'n' ? 'nurse_id' : 'physio_id', id);
                                                            handleCellChange(row.date_str, sType, idx, kind === 'n' ? 'physio_id' : 'nurse_id', undefined);
                                                            handleCellChange(row.date_str, sType, idx, 'name', member ? getNurseName(member) : '');
                                                        }}
                                                        placeholder="No Duty" onDelete={() => handleDeleteNurse(row.date_str, sType, idx)}
                                                    />
                                                );
                                            })()}
                                        </div>
                                    ))}
                                </td>
                                {/* Editable Shifts Column */}
                                <td style={styles.td}>
                                    {entries.map((e: AssignmentEntry, idx: number) => !e.is_deleted && (
                                        <input 
                                            key={idx} 
                                            type="number" 
                                            value={e.shifts || ''} 
                                            onChange={ev => handleCellChange(row.date_str, sType, idx, 'shifts', ev.target.value)} 
                                            style={{
                                                ...styles.cellInput, 
                                                height: '36px', 
                                                borderBottom: idx < entries.length - 1 ? '1px dashed #e2e8f0' : 'none'
                                            }} 
                                            placeholder="0" 
                                        />
                                    ))}
                                </td>
                                {/* 1. Editable Hrs Column */}
                                <td style={styles.td}>
                                    {entries.map((e: AssignmentEntry, idx: number) => !e.is_deleted && (
                                        <input key={idx} type="number" value={e.hrs || ''} onChange={ev => handleCellChange(row.date_str, sType, idx, 'hrs', ev.target.value)} style={{...styles.cellInput, height: '36px', borderBottom: idx < entries.length - 1 ? '1px dashed #e2e8f0' : 'none'}} placeholder="0" />
                                    ))}
                                </td>

                                {/* 2. Editable Rate Column */}
                                <td style={styles.td}>
                                    {entries.map((e: AssignmentEntry, idx: number) => !e.is_deleted && (
                                        <input key={idx} type="number" value={e.rate > 0 ? e.rate : ''} onChange={ev => handleCellChange(row.date_str, sType, idx, 'rate', ev.target.value)} style={{...styles.cellInput, height: '36px', borderBottom: idx < entries.length - 1 ? '1px dashed #e2e8f0' : 'none'}} placeholder="0" />
                                    ))}
                                </td>

                                {/* 3. Editable Amount Column */}
                                <td style={styles.td}>
                                    {entries.map((e: AssignmentEntry, idx: number) => !e.is_deleted && (
                                        <input 
                                            key={idx} 
                                            type="number" 
                                            value={e.amount || ''} // <-- Fix is here
                                            onChange={ev => handleCellChange(row.date_str, sType, idx, 'amount', ev.target.value)} 
                                            style={{
                                                ...styles.cellInput, 
                                                height: '36px', 
                                                borderBottom: idx < entries.length - 1 ? '1px dashed #e2e8f0' : 'none', 
                                                fontWeight: 600, 
                                                color: '#0f172a'
                                            }} 
                                            placeholder="0" 
                                        />
                                    ))}
                                </td>
                              </React.Fragment>
                          )
                      })}
                      <td style={{...styles.td, textAlign: 'center', fontWeight: 700}}>{activeShifts || '-'}</td>
                      <td style={{...styles.td, textAlign: 'right', paddingRight: '10px', fontWeight: 700}}>₹{totalAmount.toLocaleString()}</td>
                    </tr>
                  )
                })
              }
            </tbody>
            <tfoot style={{ backgroundColor: '#f8fafc', fontWeight: 700, borderTop: '2px solid #94a3b8' }}>
                <tr>
                    {/* We calculate the exact span: 2 for Date/Wk + (5 columns per shift) */}
                    <td colSpan={2 + (shiftsConfig.length * 5)} style={{ padding: '10px', textAlign: 'right', color: '#64748b', paddingRight: '20px' }}>
                        GRAND TOTAL
                    </td>
                    <td style={{ textAlign: 'center', padding: '10px' }}>
                        {grandTotal.shifts}
                    </td>
                    <td style={{ textAlign: 'right', padding: '10px', paddingRight: '10px' }}>
                        ₹ {grandTotal.amount.toLocaleString()}
                    </td>
                </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Casesheet;